import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Dropdown } from 'antd'
import type { MenuProps } from 'antd'
import chevronDownIcon from 'assets/images/chevron-down-24.svg'
import reminderIcon from 'assets/images/todo/property-alarm.svg'
import dayjs, { Dayjs } from 'dayjs'
import settings from 'popups/Settings'
import { format12Hours, format24Hours } from 'pages/calendar/components/EventEditor/timeFormats'
import { useTranslation } from 'react-i18next'
import { getReminderTimeStr } from '../DateFormatter'
import CalendarDatePicker from '../DatePicker'
import { DatePickerType } from '../DatePicker/DatePickerType'
import styles from './style.module.scss'
import {
  calculateReminderDateTime,
  createReminderOptions,
  getReminderTimeByDate,
} from './reminderUtils'
import type { ReminderAlarm, ReminderOption, ReminderSelectorRef } from './reminderUtils'

export { calculateReminderDateTime, createReminderOptions, getReminderTimeByDate }
export type { ReminderAlarm, ReminderSelectorRef }

interface TodoSettings {
  defaultReminderStartTime?: number
  defaultReminderTimeForToday?: number
  defaultReminderTimeForFuture?: string
}

interface UserInteraction {
  hasManuallyChangedReminder: boolean
  hasManuallyChangedDueDate: boolean
  initialLoadComplete: boolean
}

interface ReminderSelectorProps {
  value: ReminderAlarm[] | null
  onChange: (alarms: ReminderAlarm[]) => void
  disabled?: boolean
  dueDate: Date | null
  startDate?: Date | null
  onDueChange?: (date: Date | null) => void
  onStartDateChange?: (date: Date) => void
  isNew?: boolean
}

const ReminderSelector = forwardRef<ReminderSelectorRef, ReminderSelectorProps>(
  (
    {
      value,
      onChange,
      disabled,
      dueDate,
      startDate,
      onDueChange: _onDueChange,
      onStartDateChange,
      isNew = true, // 새 할일 여부 (기본값: true)
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const todoSettings = (settings.getValue('todo') as TodoSettings) || {}
    const alarms = value || []
    const [datePickerOpen, setDatePickerOpen] = useState<boolean>(false)
    const [selectedDateTime, setSelectedDateTime] = useState<Date | null>(null)

    const currentReminder = alarms.length > 0 ? alarms[0] : null

    // Use ref to track previous dueDate to avoid infinite re-renders
    const prevDueDateRef = useRef<Date | null>(null)

    // Track user interactions to distinguish manual changes from initial loads
    const userInteractionRef = useRef<UserInteraction>({
      hasManuallyChangedReminder: false,
      hasManuallyChangedDueDate: false,
      initialLoadComplete: false,
    })

    // Expose methods to parent component via ref
    useImperativeHandle(ref, () => ({
      notifyUserDueDateChange: () => {
        userInteractionRef.current.hasManuallyChangedDueDate = true
      },
    }))

    // Auto-set reminder when due date changes based on defaultReminderTimeForFuture setting
    useEffect(() => {
      // Compare Date objects properly
      const dueDateTime = dueDate ? dueDate.getTime() : null
      const prevDueDateTime = prevDueDateRef.current ? prevDueDateRef.current.getTime() : null
      const dueDateChanged = dueDateTime !== prevDueDateTime

      // Mark initial load as complete after first render
      if (!userInteractionRef.current.initialLoadComplete && dueDate !== undefined) {
        userInteractionRef.current.initialLoadComplete = true
      }

      // Auto-set reminder if:
      // 1. This is a new todo (isNew = true) - always auto-set on dueDate change
      // 2. OR this is an edit mode BUT user has manually changed dueDate after initial load
      // 3. dueDate exists and has actually changed
      // - spec out - 4. BUT NOT if user has manually changed reminder (to preserve user's choice)
      const shouldAutoSetReminder =
        dueDateChanged &&
        dueDate &&
        // - spec out -  !userInteractionRef.current.hasManuallyChangedReminder && // Don't auto-set if user manually changed reminder
        (isNew || // Always auto-set for new todos
          (userInteractionRef.current.hasManuallyChangedDueDate &&
            userInteractionRef.current.initialLoadComplete) || // Or manual dueDate change in edit mode
          (userInteractionRef.current.hasManuallyChangedReminder &&
            userInteractionRef.current.initialLoadComplete)) // Or afte
      // - spec out - userInteractionRef.current.initialLoadComplete)) // Or manual dueDate change in edit mode

      if (shouldAutoSetReminder) {
        // Get settings with proper defaults
        const defaultReminderTimeForFuture = todoSettings.defaultReminderTimeForFuture || 'dueDate'
        const defaultReminderTime = todoSettings.defaultReminderStartTime || 540 // 9AM (540 minutes)
        const defaultReminderTimeForToday = todoSettings.defaultReminderTimeForToday || 0

        // Calculate the reminder date-time based on settings
        const reminderDateTime = calculateReminderDateTime(
          dueDate,
          defaultReminderTimeForFuture,
          defaultReminderTime,
          defaultReminderTimeForToday,
        )

        if (reminderDateTime) {
          // Set the reminder directly using onChange callback
          const formattedTime = dayjs(reminderDateTime).utc().format('YYYYMMDD[T]HHmmss[Z]')

          const newAlarm: ReminderAlarm = {
            action: 'DISPLAY',
            trigger: {
              params: {},
              val: formattedTime,
            },
          }

          onChange([newAlarm])

          // Update start date if needed
          if (onStartDateChange) {
            const shouldUpdateStart =
              !startDate ||
              dayjs(startDate).format('YYYY-MM-DD HH:mm') !==
                dayjs(reminderDateTime).format('YYYY-MM-DD HH:mm')

            if (shouldUpdateStart) {
              onStartDateChange(reminderDateTime)
            }
          }
        }
      }

      // Update previous dueDate reference
      prevDueDateRef.current = dueDate
    }, [dueDate, currentReminder, onChange, onStartDateChange, startDate, isNew, todoSettings])

    const getCurrentReminderLabel = (): string => {
      if (!currentReminder) return t('todo.editor.remindeMe')

      // Get format settings
      const settingTimeFormat = settings.getValue('general').timeFormat as string
      const dateFormat = settings.getValue('general').dateFormat as string
      const is12HourFormat = settingTimeFormat === 'h:mm A'
      const formatTime = is12HourFormat ? format12Hours : format24Hours

      // 절대 시간 알람인 경우 (datetime picker로 설정된 경우)
      if (
        currentReminder.trigger &&
        typeof currentReminder.trigger === 'object' &&
        'val' in currentReminder.trigger &&
        currentReminder.trigger.val
      ) {
        // '20251001T110000Z' 형식을 올바른 ISO 8601 형식으로 변환
        const triggerVal = currentReminder.trigger.val
        let reminderDateTime: Date

        if (triggerVal && typeof triggerVal === 'string') {
          // '20251001T110000Z' 형식을 '2025-10-01T11:00:00Z' 형식으로 변환
          const formattedDate = triggerVal.replace(
            /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/,
            '$1-$2-$3T$4:$5:$6Z',
          )
          reminderDateTime = new Date(formattedDate)
        } else {
          reminderDateTime = new Date(triggerVal)
        }
        if (!isNaN(reminderDateTime.getTime())) {
          // DueDateSelector에 날짜가 설정되어 있으면 날짜 비교
          if (dueDate) {
            // dueDate와 reminder 날짜가 같으면 시간만, 다르면 날짜+시간 표시
            // Date 객체를 사용하여 날짜 부분만 비교
            const dueDateOnly = new Date(
              dueDate.getFullYear(),
              dueDate.getMonth(),
              dueDate.getDate(),
            )
            const reminderDateOnly = new Date(
              reminderDateTime.getFullYear(),
              reminderDateTime.getMonth(),
              reminderDateTime.getDate(),
            )
            const isSameDate = dueDateOnly.getTime() === reminderDateOnly.getTime()

            if (isSameDate) {
              return formatTime(reminderDateTime, null)
            } else {
              return `${dayjs(reminderDateTime).format(dateFormat)}, ${formatTime(reminderDateTime, null)}`
            }
          } else {
            // DueDateSelector에 날짜가 없으면 날짜+시간 표시
            return `${dayjs(reminderDateTime).format(dateFormat)}, ${formatTime(reminderDateTime, null)}`
          }
        }
      }

      // 상대 시간 알람인 경우 (기본 옵션들) - getReminderTimeStr 사용
      const reminderTimeStr = getReminderTimeStr(currentReminder, dueDate, startDate, t)
      if (reminderTimeStr) {
        // DueDateSelector에 날짜가 있으면 시간만 표시, 없으면 전체 옵션 레이블 표시
        if (dueDate) {
          return reminderTimeStr
        } else {
          // 상대 시간 알람의 경우 옵션 레이블을 표시
          const reminderOptions = getReminderOptions()
          // Date 객체와 문자열을 모두 비교할 수 있도록 수정
          const option = reminderOptions.find((opt) => {
            if (opt.value instanceof Date && currentReminder.trigger instanceof Date) {
              return opt.value.getTime() === (currentReminder.trigger as Date).getTime()
            }
            return opt.value === currentReminder.trigger
          })
          return option ? option.label : reminderTimeStr
        }
      }

      // 기본값
      return t('todo.editor.remindeMe')
    }

    /**
     * Handle user-initiated reminder changes (manual changes via dropdown/picker)
     */
    const handleReminderChange = (
      value: Date | string | null,
      isDateTime: boolean = false,
    ): void => {
      // Mark that user has manually changed the reminder
      userInteractionRef.current.hasManuallyChangedReminder = true
      if (value === null) {
        onChange([])
      } else if (isDateTime) {
        // 절대 시간 알람 설정
        let reminderDateTime: Dayjs
        let formattedTime: string

        if (value instanceof Date) {
          // Date 객체인 경우
          reminderDateTime = dayjs(value)
          formattedTime = reminderDateTime.utc().format('YYYYMMDD[T]HHmmss[Z]')
        } else {
          console.warn('Unsupported value type for reminder:', typeof value, value)
          return
        }

        // reminder 시간이 설정되면 dueDate와 startDate를 업데이트
        if (reminderDateTime && reminderDateTime.isValid()) {
          // 로컬 타임존으로 변환된 시간을 사용 (초는 0으로 설정)
          let targetDateTime = reminderDateTime.second(0)

          // UTC 시간을 로컬 타임존으로 변환
          if (targetDateTime.isUTC()) {
            targetDateTime = targetDateTime.local()
          }

          if (onStartDateChange) {
            const shouldUpdateStart =
              !startDate ||
              dayjs(startDate).format('YYYY-MM-DD HH:mm') !==
                targetDateTime.format('YYYY-MM-DD HH:mm')

            if (shouldUpdateStart) {
              onStartDateChange(targetDateTime.toDate())
            }
          }
        }

        onChange([
          {
            action: 'DISPLAY',
            trigger: {
              params: {},
              val: formattedTime,
            },
          },
        ])
      } else {
        // 상대 시간 알람 설정
        onChange([{ action: 'DISPLAY', trigger: value as string | Date }])
      }
    }

    const getReminderOptions = (): ReminderOption[] => {
      const hasExistingReminder = alarms.length > 0
      return createReminderOptions(t, hasExistingReminder)
    }

    const handlePickDateClick = (): void => {
      // Set initial selectedDateTime to today with defaultReminderStartTime
      const defaultReminderStartTime = todoSettings.defaultReminderStartTime || 540 // 9AM default
      const today = new Date()
      const initialDateTime = new Date(today)

      // Convert minutes to hours and minutes
      const hours = Math.floor(defaultReminderStartTime / 60)
      const minutes = defaultReminderStartTime % 60

      // Set the time to defaultReminderStartTime
      initialDateTime.setHours(hours, minutes, 0, 0)

      setSelectedDateTime(initialDateTime)
      setDatePickerOpen(true)
    }

    const handleDateTimeChange = (dateTime: Date): void => {
      // DatePicker에서 받은 dateTime이 로컬 타임존인지 확인하고 처리
      let localDateTime = dateTime

      if (dateTime instanceof Date) {
        localDateTime = dateTime
      }

      setSelectedDateTime(localDateTime)
      handleReminderChange(localDateTime, true)
      setDatePickerOpen(false)
    }

    const handleDatePickerClose = (): void => {
      setDatePickerOpen(false)
    }

    const getReminderItems = (): MenuProps['items'] => {
      const reminderOptions = getReminderOptions()
      return reminderOptions.map((option) => ({
        key: option.key,
        label: (
          <div className={styles.menuItem}>
            <div className={styles.menuItemContent}>
              <div className={styles.reminderInfo}>
                <div className={styles.reminderLabel}>{option.label}</div>
              </div>
            </div>
          </div>
        ),
        onClick: () => {
          if (option.key === 'pick_date') {
            handlePickDateClick()
          } else if (option.key === 'off') {
            handleReminderChange(null, false)
          } else {
            // Date 객체인 경우 그대로 전달, 문자열인 경우 기존 방식 유지
            handleReminderChange(option.value as Date | string, option.isDateTime)
          }
        },
      }))
    }

    return (
      <div className={styles.reminderSelector}>
        <div className={styles.titleIconWrapper}>
          <img src={reminderIcon} alt="Reminder" className={styles.titleIcon} />
        </div>
        <div className={styles.reminderPickerWrapper}>
          <Dropdown
            menu={{ items: getReminderItems() }}
            trigger={['click']}
            placement="bottomLeft"
            overlayClassName="contextMenu"
            getPopupContainer={() => document.body}
            disabled={disabled}
          >
            <button
              className={`${styles.reminderButton} ${disabled ? styles.dimmed : ''}`}
              disabled={disabled}
            >
              <div className={styles.reminderDetails}>
                <div className={styles.reminderInfo}>
                  <div className={styles.reminderLabel}>{getCurrentReminderLabel()}</div>
                </div>
              </div>
              <img src={chevronDownIcon} alt="More" className={styles.chevronIcon} />
            </button>
          </Dropdown>

          {datePickerOpen && (
            <div className={styles.datePickerOverlay} onClick={handleDatePickerClose}>
              <div onClick={(e) => e.stopPropagation()}>
                <CalendarDatePicker
                  pickerType={DatePickerType.DATETIME}
                  value={selectedDateTime}
                  onChange={handleDateTimeChange}
                  onClose={handleDatePickerClose}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    )
  },
)

ReminderSelector.displayName = 'ReminderSelector'

export default ReminderSelector
